import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ConfirmationNumber as TicketIcon,
  WatchLater as ScheduleIcon,
  Block as BlockIcon,
  Group as GroupIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { ICONS } from '../../../../shared/icons';
import { DATE_PICKER_SHORT_MONTHS, SPRINT_TIMELINE_STATUS } from '../../../../shared/appConstants';
import OverflowTooltip from '../../../../shared/OverflowTooltip';

const SprintCard = ({ sprint, onTicketCountClick, onBlockedCountClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = (sprint?.status || SPRINT_TIMELINE_STATUS.DRAFT).toLowerCase();

  const headerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${DATE_PICKER_SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
  };

  const formattedStartDate = formatDate(sprint.start_date);
  const formattedEndDate = formatDate(sprint.end_date);
  const goal = sprint.goal || '';
  const storyPoints = Number(
    sprint.total_story_point ??
      sprint.total_story_points ??
      sprint.story_points ??
      0
  );
  const blockedCount = Number(sprint.blocked_count ?? sprint.health?.blocked_ticket_count ?? 0);
  const memberCount = Number(sprint.member_count || 0);
  const doneCount = Number(sprint.complete_ticket ?? sprint.done_ticket_count ?? 0);
  const rolloverCount = Number(sprint.roll_over_count || 0);
  
  const ticketCount = Number(sprint.ticket_count || 0);

  const startsInDays = Number(sprint.starts_in_days ?? 0);
  const isTicketCountClickable = typeof onTicketCountClick === 'function' && ticketCount > 0;
  const isBlockedCountClickable = typeof onBlockedCountClick === 'function' && blockedCount > 0;

  const handleTicketCountClick = (event) => {
    event.stopPropagation();
    if (!isTicketCountClickable) return;
    onTicketCountClick(sprint);
  };

  const handleBlockedCountClick = (event) => {
    event.stopPropagation();
    if (!isBlockedCountClickable) return;
    onBlockedCountClick(sprint);
  };

  const progressPercent = ticketCount > 0 ? (doneCount / ticketCount) * 100 : 0;
  const statusLabelKey = `sprintTimeline.${status}Sprint`;

  const handleSprintNameClick = (e) => {
    e.stopPropagation();
    navigate('/sprint-management');
  };

  return (
    <div 
      id={`sprint-card-${sprint.id}`}
      className={`sprint-card ${status === SPRINT_TIMELINE_STATUS.ACTIVE ? 'active' : ''}`}
    >
      <div className="card-header">
        <div ref={headerRef}>
          <OverflowTooltip key={containerWidth} title={sprint.name} component="h3" className={`sprint-name ${status === SPRINT_TIMELINE_STATUS.ACTIVE ? 'navigable' : ''}`} placement="top-start" tooltipWidth={400} onClick={status === SPRINT_TIMELINE_STATUS.ACTIVE ? handleSprintNameClick : undefined}>
            {sprint.name}
          </OverflowTooltip>
          {goal && (
            <p className="sprint-goal">{goal}</p>
          )}
        </div>
        <div className="chips-container">
          <span className={`chip sp-chip ${status === SPRINT_TIMELINE_STATUS.ACTIVE ? 'status-active' : ''}`}>
            {t('sprintTimeline.spCount', { count: storyPoints })}
          </span>
          <span className={`chip status-${status}`}>
            {t(statusLabelKey).split(' ')[0]}
          </span>
        </div>
      </div>

      <div className="card-details">
        <div className="detail-item">
          <img src={ICONS.dueDate} alt="Date" className="icon date-icon" />
          <span>{`${formattedStartDate} - ${formattedEndDate}`}</span>
        </div>

        <button
          type="button"
          className={`detail-item ticket-count-button ${isTicketCountClickable ? 'clickable' : ''}`}
          onClick={handleTicketCountClick}
          disabled={!isTicketCountClickable}
        >
          <TicketIcon className="icon" fontSize="small" />
          <span>{t('sprintTimeline.tickets', { count: ticketCount })}</span>
        </button>

        {status === SPRINT_TIMELINE_STATUS.DRAFT && (
          <div className="detail-item schedule-detail">
            <ScheduleIcon className="icon schedule-icon" fontSize="small" />
            <span>
              {startsInDays === 1
                ? t('sprintTimeline.startsIn', { days: startsInDays })
                : t('sprintTimeline.startsInPlural', { days: startsInDays })}
            </span>
          </div>
        )}

        {status === SPRINT_TIMELINE_STATUS.ACTIVE && (
          <>
            <button
              type="button"
              className={`detail-item blocked-detail blocked-count-button ${isBlockedCountClickable ? 'clickable' : ''}`}
              onClick={handleBlockedCountClick}
              disabled={!isBlockedCountClickable}
            >
              <BlockIcon className="icon blocked-icon" fontSize="small" />
              <span>{t('sprintTimeline.blockedCount', { count: blockedCount })}</span>
            </button>
            <div className="detail-item">
              <GroupIcon className="icon" fontSize="small" />
              <span>{t('sprintTimeline.memberCount', { count: memberCount })}</span>
            </div>
            <div className="progress-container">
              <div className="progress-text">
                {t('sprintTimeline.progressTickets', { done: doneCount, total: ticketCount })}
              </div>
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </>
        )}

        {status === SPRINT_TIMELINE_STATUS.COMPLETED && (
          <div className="detail-item">
            <CheckIcon className="icon" fontSize="small" />
            <span>{t('sprintTimeline.doneSummary', { done: doneCount, total: doneCount + rolloverCount })}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SprintStepper = ({ sprints = [], onTicketCountClick, onBlockedCountClick }) => {
  if (!Array.isArray(sprints) || sprints.length === 0) return null;

  return (
    <div className="sprint-stepper">
      {sprints.map((sprint) => {
        const status = (sprint?.status || SPRINT_TIMELINE_STATUS.DRAFT).toLowerCase();

        return (
          <div key={sprint.id} className="stepper-item">
            <div className={`stepper-indicator status-${status}`} />
            <SprintCard
              sprint={sprint}
              onTicketCountClick={onTicketCountClick}
              onBlockedCountClick={onBlockedCountClick}
            />
          </div>
        );
      })}
    </div>
  );
};

export default SprintStepper;
